/**
 * Native Capacitor integrations — used by every page that needs to open an
 * external link, share content, or hook into Android system events.
 *
 * Web fall-back is the original behaviour (`window.open`, native share or
 * clipboard copy). On Android the same call routes through the appropriate
 * Capacitor plugin so the WebView never traps the user.
 */
import { isNativePlatform } from "./platform";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Share } from "@capacitor/share";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Network } from "@capacitor/network";

let _appListenersMounted = false;
let _backHandlerStack = [];

// Kept as an async function so existing call-sites `await _capModules()` still
// work without diff churn — but the imports themselves are now STATIC so the
// Capacitor bridge can never deadlock on a dynamic chunk load (observed on
// Android 16 with the older `await import(...)` pattern).
async function _capModules() {
  return { App, Browser, Share, SplashScreen, StatusBar, Style, Network };
}

/**
 * Open an arbitrary URL — chooses the best native target on Android:
 *   • `https://wa.me/…`  → `whatsapp://send?phone=…&text=…` so the native
 *     WhatsApp app launches directly (no in-app browser pit-stop).
 *   • `tel:`, `mailto:`, `sms:` → `App.openUrl` (system handler).
 *   • Other `https://` URLs → external Chrome / default browser.
 *
 * On web → `window.open(url, "_blank")`.
 */
export async function openExternal(url) {
  if (!url) return;
  if (!isNativePlatform()) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  try {
    const { App, Browser } = await _capModules();
    // WhatsApp deep link conversion
    const wa = url.match(/^https:\/\/wa\.me\/(\d+)(?:\?(.+))?$/i);
    if (wa) {
      const phone = wa[1];
      const params = new URLSearchParams(wa[2] || "");
      const text = params.get("text") || "";
      const deep = `whatsapp://send?phone=${phone}${text ? `&text=${encodeURIComponent(text)}` : ""}`;
      try {
        await App.openUrl({ url: deep });
        return;
      } catch {
        // Fallback to wa.me in default browser (e.g. WhatsApp not installed)
      }
    }
    // tel:, mailto:, sms: → system dialer / mail / sms
    if (/^(tel|mailto|sms):/i.test(url)) {
      await App.openUrl({ url });
      return;
    }
    // Everything else → default browser (out of the WebView)
    await Browser.open({ url, presentationStyle: "fullscreen" });
  } catch (err) {
    console.warn("openExternal failed:", err?.message || err);
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

/** One-tap dial — `tel:0300...` */
export async function callPhone(rawMobile) {
  if (!rawMobile) return;
  const digits = String(rawMobile).replace(/[^\d+]/g, "");
  if (!digits) return;
  await openExternal(`tel:${digits}`);
}

/**
 * Native share sheet. Falls back to navigator.share, then clipboard, then a
 * new tab on the web.
 *
 *   shareItem({ title, text, url, dialogTitle })
 */
export async function shareItem({ title, text, url, dialogTitle } = {}) {
  if (isNativePlatform()) {
    try {
      const { Share } = await _capModules();
      await Share.share({
        title: title || "HisaabPro",
        text: text || "",
        url: url || undefined,
        dialogTitle: dialogTitle || "Share via",
      });
      return true;
    } catch (err) {
      console.warn("native share failed:", err?.message || err);
    }
  }
  // Web — prefer navigator.share (mobile browsers), else clipboard
  try {
    if (navigator.share) {
      await navigator.share({ title, text, url });
      return true;
    }
  } catch (err) {
    if (err?.name !== "AbortError") console.warn("navigator.share failed:", err);
  }
  try {
    if (navigator.clipboard && (url || text)) {
      await navigator.clipboard.writeText(url || text);
      return "copied";
    }
  } catch {/* ignore */}
  if (url) window.open(url, "_blank");
  return false;
}

/**
 * Push a custom Android back-button handler. Returns an unregister function.
 *
 * Stack semantics — the most recently pushed handler runs first; if it
 * returns `true` the event is consumed, otherwise we fall back to the
 * default (exit the app at the root route).
 */
export function pushBackHandler(handler) {
  _backHandlerStack.push(handler);
  return () => {
    const i = _backHandlerStack.lastIndexOf(handler);
    if (i !== -1) _backHandlerStack.splice(i, 1);
  };
}

/**
 * Wire up Android lifecycle hooks: status bar, splash screen, back button,
 * network listener, and a delegated click interceptor that routes every
 * `<a target="_blank">` (and `wa.me` / `tel:` href anywhere in the DOM)
 * through `openExternal()`. Idempotent — safe to call multiple times.
 */
export async function setupAndroidLifecycle({ onNetworkChange } = {}) {
  if (!isNativePlatform() || _appListenersMounted) return;
  _appListenersMounted = true;

  // Delegated click handler — catches every external-link tap in the WebView
  // before the browser's default navigation fires.
  document.addEventListener("click", (ev) => {
    const a = ev.target?.closest?.("a[href]");
    if (!a) return;
    const href = a.getAttribute("href") || "";

    // PDFs (own backend or relative) → download via authenticated api client
    // and open with the device's default PDF viewer.
    const isPdf =
      a.dataset?.nativePdf === "true" ||
      /\.pdf(\?|$)/i.test(href) ||
      /\/(agreements|customers|payments|loans)\/[^\s]+\/(pdf|ledger\.pdf|receipt\.pdf)/i.test(href);
    if (isPdf) {
      ev.preventDefault();
      const fname =
        a.dataset?.filename ||
        (href.match(/\/([^/?#]+\.pdf)/i)?.[1] || a.textContent?.trim() || "HisaabPro-document");
      // Toast progress
      Promise.all([
        import("../lib/nativePdf"),
        import("sonner"),
      ]).then(([{ downloadAndOpenPdf }, { toast }]) => {
        const tId = toast.loading("PDF download ho rahi hai…");
        downloadAndOpenPdf(href, {
          filename: fname.replace(/\.pdf$/i, ""),
          onProgress: (frac) => {
            toast.loading(`PDF download: ${Math.round(frac * 100)}%`, { id: tId });
          },
        })
          .then(() => toast.success("PDF khul gayi", { id: tId }))
          .catch((err) => toast.error(err?.response?.data?.detail || err?.message || "PDF download fail", { id: tId }));
      });
      return;
    }

    const isExternal =
      a.target === "_blank" ||
      /^https:\/\/wa\.me\//i.test(href) ||
      /^(tel|mailto|sms|whatsapp):/i.test(href) ||
      (/^https?:\/\//i.test(href) && !href.startsWith(window.location.origin));
    if (!isExternal) return;
    ev.preventDefault();
    openExternal(href);
  }, true);

  // Patch `window.open` so existing call-sites (Referral share, Settings,
  // recovery dialer) route through the native handler.
  const _origOpen = window.open;
  window.open = function (url, target, features) {
    if (url && /^(https?|wa|tel|mailto|sms|whatsapp):/i.test(String(url))) {
      openExternal(String(url));
      return null;
    }
    return _origOpen.call(window, url, target, features);
  };

  try {
    const { App, SplashScreen, StatusBar, Style, Network } = await _capModules();

    // Status bar: emerald brand + light text. On Android 15 (SDK 35) edge-to-edge
    // is enforced, so we also tell the plugin NOT to overlay the WebView — this
    // pushes our content below the status bar instead of underneath it.
    try {
      await StatusBar.setOverlaysWebView({ overlay: false });
    } catch {/* iOS / older Android ignore */}
    try {
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setBackgroundColor({ color: "#059669" });
    } catch {/* not all OEMs honour setBackgroundColor */}

    // Hide splash once React has mounted
    setTimeout(() => SplashScreen.hide().catch(() => {}), 300);

    // Back button → run top of stack, else navigate back, else exit app
    App.addListener("backButton", ({ canGoBack }) => {
      if (_backHandlerStack.length) {
        const top = _backHandlerStack[_backHandlerStack.length - 1];
        try { if (top()) return; } catch (e) { console.warn("back handler error:", e); }
      }
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });

    // Network state → bubble to caller (App-level toast)
    if (typeof onNetworkChange === "function") {
      Network.addListener("networkStatusChange", (status) => {
        onNetworkChange(status.connected, status.connectionType);
      });
      const initial = await Network.getStatus();
      onNetworkChange(initial.connected, initial.connectionType);
    }
  } catch (err) {
    console.warn("setupAndroidLifecycle failed:", err?.message || err);
  }
}
